import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { CheckCircle, ArrowRight, Package, Home, ShoppingBag } from "lucide-react";
import { useState } from "react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState("checking");
  const [orderDetails, setOrderDetails] = useState(null);
  const sessionId = searchParams.get("session_id");
  const isCartOrder = searchParams.get("cart") === "true";

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/public/checkout/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          setStatus("success");
          setOrderDetails({
            isCartOrder: data.is_cart_checkout,
            items: data.items || [],
            total: data.amount_total ? data.amount_total / 100 : 0,
            orderId: data.order_id
          });
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-lg">
          {status === "checking" ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6">
                <div className="w-full h-full border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Processing Payment
              </h1>
              <p className="text-slate-500 mb-6">
                Please wait while we confirm your payment...
              </p>
            </>
          ) : status === "success" ? (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="payment-success">
                Payment Successful!
              </h1>
              <p className="text-slate-500 mb-4">
                Thank you for your purchase. Your order has been confirmed.
              </p>
              
              {/* Order Details for Cart Orders */}
              {orderDetails && orderDetails.items && orderDetails.items.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left" data-testid="order-summary">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4" />
                    Order Summary
                  </h3>
                  <div className="space-y-2">
                    {orderDetails.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">
                          {item.name} x {item.quantity}
                        </span>
                        <span className="font-medium text-slate-900">
                          £{(item.total || item.unit_price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                      <span className="text-slate-700">Total</span>
                      <span className="text-indigo-600">
                        £{orderDetails.items.reduce((sum, item) => sum + (item.total || item.unit_price * item.quantity), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {orderDetails.orderId && (
                    <p className="text-xs text-slate-500 mt-3">
                      Order ID: {orderDetails.orderId}
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex flex-col gap-3">
                <Link to="/dashboard">
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full h-12" data-testid="view-orders-btn">
                    <Package className="h-5 w-5 mr-2" />
                    View My Orders
                  </Button>
                </Link>
                <Link to="/store">
                  <Button variant="outline" className="w-full rounded-full h-12" data-testid="continue-shopping-btn">
                    Continue Shopping
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Package className="h-10 w-10 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Payment Status Unknown
              </h1>
              <p className="text-slate-500 mb-8">
                We couldn't confirm your payment status. Please check your email for confirmation or contact support.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/dashboard">
                  <Button className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full h-12">
                    <Package className="h-5 w-5 mr-2" />
                    Check Orders
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full rounded-full h-12">
                    <Home className="h-5 w-5 mr-2" />
                    Return Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
